import React from "react";
import { Modal } from "../components/Modal";
import {
  getBilling,
  getUsageValue,
  openBillingPortal,
  startBillingCheckout,
} from "../lib/api";
import type { AuthHeaders } from "../lib/auth";
import type {
  Billing as BillingData,
  BillingTier,
  UsageValue,
} from "../lib/types";
import { fmtDate, humanize } from "../lib/format";
import { getHashParam, setHashParam } from "../lib/hashState";
import { LITE_CAP_TIP, TOKENS_SAVED_CAP_TIP } from "../lib/copy";
import {
  Callout,
  Button,
  Card,
  EmptyState,
  Grid,
  Icon,
  IconTile,
  LinkButton,
  Page,
  Pill,
  SectionHead,
  Segmented,
  Skeleton,
  StatTile,
  Tooltip,
  type IconName,
  type Tone,
} from "../design";
import { presetRange } from "./insights/range";
import { valueTile } from "./planValue";

const TIER_META: Record<string, { name: string }> = {
  "tier-free": { name: "Free" },
  t5: { name: "Starter" },
  t20: { name: "Pro" },
  t50: { name: "Max" },
};

const tierName = (t: string): string => TIER_META[t]?.name ?? t;

// Each tier shows how far it stretches the provider plan it fits:
// condense roughly triples reach, 1→3, 5→15, 20→60.
const TIER_RANK: Record<string, number> = { t5: 1, t20: 2, t50: 3 };
const TIER_XFORM: Record<string, { from: number; to: number }> = {
  t5: { from: 1, to: 3 },
  t20: { from: 5, to: 15 },
  t50: { from: 20, to: 60 },
};

// The agent only changes the matcher's plan names, not condense's tiers.
// OpenCode has no plan ladder, so it drops the matcher and features $20.
type AgentKey = "claude" | "codex" | "opencode";
interface AgentDef {
  brand: string;
  matcherLabel: string;
  plans: string[]; // ranked by index (1..3); empty = no matcher
}
const AGENTS: Record<AgentKey, AgentDef> = {
  claude: {
    brand: "Claude",
    matcherLabel: "Which Claude plan are you on?",
    plans: ["Pro $20", "Max $100", "Max $200"],
  },
  codex: {
    brand: "Codex",
    matcherLabel: "Which Codex plan are you on?",
    plans: ["Plus $20", "Pro $100", "Pro $200"],
  },
  opencode: { brand: "OpenCode", matcherLabel: "", plans: [] },
};
const AGENT_ORDER: AgentKey[] = ["claude", "codex", "opencode"];

const PLAN_FEATURES: React.ReactNode[] = [
  <>Realtime savings dashboard</>,
  <>
    Works with <b>Claude Code, Codex &amp; OpenCode</b>
  </>,
  <>
    Cache-safe: <b>98.6%</b> provider cache hits
  </>,
  <>
    <b>7-day</b> retention &middot; optional ZDR
  </>,
];

function isSubscribed(b: BillingData): boolean {
  // past_due still counts: Stripe keeps retrying the charge and the
  // subscription runs until retries exhaust and it flips to canceled/unpaid.
  // An operator-granted paid tier has no Stripe row but is a plan all the same.
  const price = b.tiers.find((t) => t.tier === b.tier)?.price_usd ?? 0;
  return (
    b.status === "active" ||
    b.status === "trialing" ||
    b.status === "past_due" ||
    (!b.status && price > 0)
  );
}

// Stripe retains the previous tier after a subscription ends. It is purchase
// history, not a current plan; these terminal states need a fresh checkout.
function hasEnded(b: BillingData): boolean {
  return b.status === "canceled" || b.status === "incomplete_expired";
}

// Condense Lite: the weekly pool is spent and the same plan keeps compressing
// under a savings ceiling. A state every tier can be in, never a tier itself.
function isLite(b: BillingData): boolean {
  return b.lane === "lite";
}

function pct(used: number, cap: number): number {
  if (cap <= 0) return 0;
  return Math.min(100, Math.round((used / cap) * 100));
}

function statusLabel(status: string | null): string {
  if (status === "active") return "Active";
  if (status === "trialing") return "Trialing";
  if (status === "past_due") return "Past due";
  if (status === "unpaid") return "Unpaid";
  if (status === "canceled") return "Canceled";
  if (status === "incomplete_expired") return "Expired";
  return status ?? "No subscription";
}

function statusPill(status: string): { tone: Tone; icon: IconName } {
  if (status === "active") return { tone: "ok", icon: "check" };
  if (status === "trialing") return { tone: "info", icon: "clock" };
  if (status === "past_due" || status === "unpaid")
    return { tone: "warn", icon: "alert" };
  return { tone: "neutral", icon: "minus" };
}

// Tier one step above `current` by price, for the upgrade nudge.
function nextTierUp(
  tiers: BillingTier[],
  current: string | null,
): BillingTier | null {
  const sorted = [...tiers].sort((a, b) => a.price_usd - b.price_usd);
  const cur = sorted.find((t) => t.tier === current);
  if (!cur) return sorted[0] ?? null;
  return sorted.find((t) => t.price_usd > cur.price_usd) ?? null;
}

const PORTAL_ERR =
  "Couldn't open the billing portal. Try again in a minute; if it keeps failing, email team@condense.chat and we'll make the change for you.";

const CHECKOUT_ERR =
  "Couldn't open the payment page. Nothing was charged; try again in a minute.";

function Notice({
  title,
  children,
  warn,
  action,
}: {
  title?: string;
  children: React.ReactNode;
  warn?: boolean;
  action?: React.ReactNode;
}) {
  return (
    <Callout tone={warn ? "warn" : "info"} title={title} action={action}>
      {children}
    </Callout>
  );
}

export function PlanHeader({
  billing,
  authHeaders,
}: {
  billing: BillingData;
  authHeaders: AuthHeaders;
}) {
  const subscribed = isSubscribed(billing);
  const tier = billing.tiers.find((t) => t.tier === billing.tier);
  const canManage = billing.manage_available;
  type Flow = "cancel" | "update_payment" | "manage";
  const [portalBusy, setPortalBusy] = React.useState<Flow | null>(null);
  const [portalErr, setPortalErr] = React.useState(false);
  const [confirmCancel, setConfirmCancel] = React.useState(false);
  const freeCap =
    billing.tiers.find((t) => t.tier === "tier-free")?.week_cap ?? 0;

  async function openPortal(flow: Flow) {
    if (portalBusy) return;
    setPortalBusy(flow);
    setPortalErr(false);
    try {
      window.location.href = await openBillingPortal(
        authHeaders,
        flow === "manage" ? undefined : flow,
      );
    } catch {
      setPortalErr(true);
      setPortalBusy(null);
    }
  }

  const isFree = billing.tier === "tier-free";
  const pastDue = billing.status === "past_due";
  // unpaid = Stripe exhausted its retries but kept the subscription; the
  // plan still exists, payment just needs fixing — same remedy as past_due.
  const paymentFailed = pastDue || billing.status === "unpaid";
  const canCancel =
    canManage &&
    subscribed &&
    !!billing.status &&
    !isFree &&
    !billing.cancel_at_period_end;
  const refill = billing.week_resets_at
    ? ` until it refills on ${fmtDate(billing.week_resets_at)}`
    : "";
  let note: { text: string; warn?: boolean } | null = null;
  if (hasEnded(billing)) {
    note = {
      text: "Your subscription has ended. Choose a plan below to resubscribe and restore paid access.",
      warn: true,
    };
  } else if (pastDue) {
    note = {
      text: "Your last payment failed. We'll retry it automatically over the next few days. Please check your payment method to keep your plan.",
      warn: true,
    };
  } else if (billing.status === "unpaid") {
    note = {
      text: "Your plan is on hold: we couldn't collect payment. Update your payment method to resume compression.",
      warn: true,
    };
  } else if (subscribed && billing.renews_at && billing.cancel_at_period_end) {
    note = {
      text: `Cancels on ${fmtDate(billing.renews_at)}, access continues until then.`,
      warn: true,
    };
  } else if (isLite(billing)) {
    note =
      billing.week_cap > 0
        ? {
            text: `Your weekly pool is spent. Condense Lite keeps compressing at up to 20% of each prompt${refill}.`,
            warn: true,
          }
        : {
            text: "Condense Lite is compressing your sessions at up to 20% of each prompt. Choose a plan below for full strength.",
          };
  } else if (!billing.compressing && billing.week_cap > 0) {
    note = {
      text: `Your weekly pool is spent. Condense Lite keeps compressing at up to 20% of each prompt${refill}.`,
      warn: true,
    };
  } else if (subscribed && billing.renews_at) {
    note = tier
      ? {
          text: `Next charge $${tier.price_usd} on ${fmtDate(billing.renews_at)}.`,
        }
      : { text: `Renews on ${fmtDate(billing.renews_at)}.` };
  } else if (isFree) {
    note = {
      text: `${humanize(billing.week_cap)} tokens compressed every week, free forever. Then Lite at up to 20%, unmetered.`,
    };
  } else if (!subscribed && !billing.compressing) {
    note = billing.trial_available
      ? {
          text: "Your sessions are passing straight through. Choose a plan below to start your 14-day free trial.",
        }
      : {
          text: "Your sessions are passing straight through. Choose a plan below to turn compression back on.",
        };
  }

  const status = billing.status ? statusPill(billing.status) : null;
  return (
    <Card
      title="Plan"
      icon={<IconTile icon="subscription" tone="blue" />}
      actions={
        <>
          {hasEnded(billing) && (
            <LinkButton variant="primary" size="sm" href="#plans">
              Resubscribe
            </LinkButton>
          )}
          {canManage && paymentFailed && (
            <Button
              size="sm"
              onClick={() => openPortal("update_payment")}
              busy={portalBusy === "update_payment"}
            >
              {portalBusy === "update_payment"
                ? "Opening…"
                : "Update payment method"}
            </Button>
          )}
          {canCancel && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setConfirmCancel(true)}
              busy={portalBusy === "cancel"}
            >
              {portalBusy === "cancel" ? "Opening…" : "Cancel plan"}
            </Button>
          )}
          {confirmCancel && (
            <Modal
              title={`Cancel ${billing.tier ? tierName(billing.tier) : "your plan"}?`}
              onClose={() => setConfirmCancel(false)}
              footer={
                <>
                  <Button onClick={() => setConfirmCancel(false)}>
                    Keep my plan
                  </Button>
                  <Button
                    variant="danger"
                    busy={portalBusy === "cancel"}
                    onClick={() => void openPortal("cancel")}
                  >
                    Continue to cancel
                  </Button>
                </>
              }
            >
              <p className="per-muted">
                {billing.renews_at
                  ? `You keep everything on your plan until ${fmtDate(billing.renews_at)}. `
                  : ""}
                {freeCap > 0
                  ? `After that you move to Free: ${humanize(freeCap)} tokens compressed a week, then Condense Lite at up to 20% of each prompt.`
                  : "After that Condense Lite keeps compressing at up to 20% of each prompt."}{" "}
                Your history and settings stay, and you confirm the cancellation
                on the payment provider&apos;s page.
              </p>
            </Modal>
          )}
          {canManage && (
            <Button
              size="sm"
              variant="ghost"
              iconRight="arrow-up-right"
              onClick={() => openPortal("manage")}
              busy={portalBusy === "manage"}
            >
              {portalBusy === "manage" ? "Opening…" : "Manage billing"}
            </Button>
          )}
        </>
      }
    >
      <div className="per-plan">
        <span className="per-plan-name">
          {billing.tier ? tierName(billing.tier) : "No plan"}
        </span>
        {tier && subscribed && billing.status && (
          <span className="per-plan-price">${tier.price_usd} / month</span>
        )}
        {status && billing.status && (
          <Pill tone={status.tone} icon={status.icon}>
            {statusLabel(billing.status)}
          </Pill>
        )}
        {isLite(billing) && (
          <Pill tone="info" icon="sparkle">
            Condense Lite
          </Pill>
        )}
      </div>
      {note && (
        <p className={`per-plan-note${note.warn ? " warn" : ""}`}>
          {note.warn && <Icon name="alert" size={14} />}
          {note.text}
        </p>
      )}
      {portalErr && (
        <div className="per-err" role="alert">
          {PORTAL_ERR}
        </div>
      )}
    </Card>
  );
}

function PlanCapacity({ billing }: { billing: BillingData }) {
  if (!billing.tier) return null;
  const p = pct(billing.week_saved, billing.week_cap);
  // Fill width is a precise float (not the rounded `p`), so sub-1% usage of a
  // large cap still renders a growing sliver instead of collapsing to 0.
  const fillW =
    billing.week_cap > 0 && billing.week_saved > 0
      ? Math.min(
          100,
          Math.max(0.5, (billing.week_saved / billing.week_cap) * 100),
        )
      : 0;
  const color =
    p >= 100 ? "var(--ds-bad)" : p >= 80 ? "var(--ds-warn)" : "var(--ds-s1)";
  const resets = billing.week_resets_at
    ? new Date(billing.week_resets_at).toLocaleDateString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
      })
    : null;
  return (
    <StatTile
      label={billing.is_trial ? "Trial allowance" : "Allowance this week"}
      info={TOKENS_SAVED_CAP_TIP}
      value={`${p}% used`}
      note={
        !billing.is_trial && resets
          ? `${humanize(billing.week_saved)} of ${humanize(billing.week_cap)} tokens · resets ${resets}`
          : `${humanize(billing.week_saved)} of ${humanize(billing.week_cap)} tokens`
      }
      spark={
        <span
          className="per-meter"
          role="meter"
          aria-label="Share of this week's allowance used"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(fillW)}
        >
          <span style={{ width: `${fillW}%`, background: color }} />
        </span>
      }
    />
  );
}

// Pool spent with Lite carrying the sessions, or close to the cap: what it
// still delivers and the way back to full strength.
function CapacityNotices({ billing }: { billing: BillingData }) {
  if (!billing.tier) return null;
  const weekP = pct(billing.week_saved, billing.week_cap);
  const up = nextTierUp(billing.tiers, billing.tier);
  const upgrade = up && (
    <LinkButton size="sm" href="#plans" iconRight="arrow-right">
      Upgrade to {tierName(up.tier)}
    </LinkButton>
  );
  if (isLite(billing)) {
    const saved = Math.max(0, billing.lane_saved ?? 0);
    return (
      <Notice
        title="Condense Lite is compressing your sessions"
        action={upgrade}
      >
        Your weekly pool is spent, so {tierName(billing.tier)} keeps saving
        around 20% of each prompt instead of the usual 55&ndash;68%.
        {saved > 0 && ` ${humanize(saved)} tokens saved on Lite so far.`}
        {billing.week_resets_at &&
          ` Full strength returns ${fmtDate(billing.week_resets_at)}.`}
      </Notice>
    );
  }
  if (weekP >= 80 && up)
    return (
      <Notice action={upgrade}>
        You&apos;ve used {weekP}% of your weekly capacity.
      </Notice>
    );
  return null;
}

function TierCard({
  tier,
  current,
  subscribed,
  trialing,
  trialAvailable = false,
  resubscribe = false,
  currentPrice,
  matchState,
  showMatchNote = true,
  xform,
  xformCapLabel = "your plan",
  renewsAt = null,
  onSelect,
  featured = false,
  authHeaders,
}: {
  tier: BillingTier;
  current: boolean;
  subscribed: boolean;
  trialing?: boolean;
  trialAvailable?: boolean;
  resubscribe?: boolean;
  currentPrice: number;
  matchState?: "matched" | "dimmed";
  showMatchNote?: boolean;
  xform?: { from: number; to: number };
  xformCapLabel?: string;
  renewsAt?: string | null;
  onSelect?: () => void;
  featured?: boolean;
  authHeaders: AuthHeaders;
}) {
  const isFreeTier = tier.tier === "tier-free";
  const isUpgrade = subscribed && !current && tier.price_usd > currentPrice;
  const isDowngrade = subscribed && !current && tier.price_usd < currentPrice;
  const [checkoutBusy, setCheckoutBusy] = React.useState(false);
  const [checkoutErr, setCheckoutErr] = React.useState<string | null>(null);
  const [confirm, setConfirm] = React.useState<"trial" | "switch" | null>(null);

  async function startCheckout() {
    if (checkoutBusy) return;
    setConfirm(null);
    setCheckoutBusy(true);
    setCheckoutErr(null);
    try {
      const url = await startBillingCheckout(authHeaders, tier.tier);
      window.location.href = url;
      // Switching an existing subscription happens server-side and returns our
      // own dashboard, where a hash-only change wouldn't refetch the new plan.
      if (url.startsWith(window.location.origin)) window.location.reload();
    } catch {
      setCheckoutErr(CHECKOUT_ERR);
      setCheckoutBusy(false);
    }
  }

  // Free has no Stripe price — "downgrading" to it means canceling the
  // paid subscription, so route through the portal's cancel flow instead
  // of checkout (which 400s for tier-free).
  async function cancelToFree() {
    if (checkoutBusy) return;
    setCheckoutBusy(true);
    setCheckoutErr(null);
    try {
      window.location.href = await openBillingPortal(authHeaders, "cancel");
    } catch {
      setCheckoutErr(PORTAL_ERR);
      setCheckoutBusy(false);
    }
  }

  // A switch applies at once (mid-trial it also ends the trial and bills),
  // so confirm the price and timing first.
  function onCta() {
    if (isFreeTier && isDowngrade) cancelToFree();
    else if (trialing) setConfirm("trial");
    else if (isUpgrade || isDowngrade) setConfirm("switch");
    else startCheckout();
  }

  const cls = [
    "per-tier",
    current ? "is-current" : "",
    onSelect ? "is-selectable" : "",
    matchState === "matched" ? "is-matched" : "",
    matchState === "dimmed" ? "is-dimmed" : "",
  ]
    .filter(Boolean)
    .join(" ");
  const ctaLabel = resubscribe
    ? "Resubscribe"
    : isUpgrade
      ? "Upgrade"
      : isDowngrade
        ? "Downgrade"
        : `Choose ${tierName(tier.tier)}`;
  const ctaPrimary = (isUpgrade && featured) || matchState === "matched";
  // Clicking the card body selects the plan; the CTA keeps its own action.
  const handleClick = onSelect
    ? (e: React.MouseEvent) => {
        if ((e.target as HTMLElement).closest("a,button")) return;
        onSelect();
      }
    : undefined;
  return (
    <div className={cls} onClick={handleClick}>
      <div className="per-tier-head">
        <span className="per-tier-name">{tierName(tier.tier)}</span>
        {current ? (
          <Pill tone="info" icon="check">
            Current plan
          </Pill>
        ) : (
          matchState === "matched" &&
          showMatchNote && (
            <Pill tone="ok" icon="check">
              Your match
            </Pill>
          )
        )}
      </div>
      <div className="per-tier-price">
        <b>${tier.price_usd}</b>
        <span>/month</span>
      </div>
      {xform && (
        <>
          <div className="per-tier-xf">
            <s>{xform.from}×</s>
            <Icon name="arrow-right" size={14} />
            <b>{xform.to}×</b>
            <span className="per-tier-xf-cap">{xformCapLabel}</span>
          </div>
          <dl className="per-tier-lanes">
            <div>
              <dt>
                <span className="per-tier-lane">Premium</span>
                <span>up to 68% off</span>
              </dt>
              <dd>
                <Tooltip text={TOKENS_SAVED_CAP_TIP}>
                  <span className="per-tip">
                    <b>{humanize(tier.week_cap)}</b> tokens
                  </span>
                </Tooltip>{" "}
                compressed a week
              </dd>
            </div>
            <div>
              <dt>
                <span className="per-tier-lane lite">Lite</span>
                <span>up to 20% off</span>
              </dt>
              <dd>
                <Tooltip text={LITE_CAP_TIP}>
                  <span className="per-tip">
                    <b>Unlimited</b>
                  </span>
                </Tooltip>{" "}
                after that, never runs out
              </dd>
            </div>
          </dl>
        </>
      )}
      {(isFreeTier || trialAvailable) && (
        <div className="per-tier-trial">
          {isFreeTier ? (
            <>
              <b>Free forever</b> &middot; no card required
            </>
          ) : (
            <>
              <b>14-day</b> free trial &middot;{" "}
              <b>{humanize(tier.trial_cap)}</b> tokens free
            </>
          )}
        </div>
      )}
      <ul className="per-tier-features">
        {PLAN_FEATURES.map((f, i) => (
          <li key={i}>
            <Icon name="check" size={14} />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      {!current && (
        <div className="per-tier-cta">
          <Button
            variant={ctaPrimary ? "primary" : "secondary"}
            onClick={onCta}
            busy={checkoutBusy}
          >
            {checkoutBusy ? "Opening…" : ctaLabel}
          </Button>
          {checkoutErr && (
            <div className="per-err" role="alert">
              {checkoutErr}
            </div>
          )}
        </div>
      )}
      {confirm === "trial" && (
        <Modal
          title={`Switch to ${tierName(tier.tier)}`}
          onClose={() => setConfirm(null)}
          footer={
            <>
              <Button variant="ghost" onClick={() => setConfirm(null)}>
                Keep my trial
              </Button>
              <Button
                variant="primary"
                onClick={startCheckout}
                busy={checkoutBusy}
              >
                {checkoutBusy ? "Opening…" : "Proceed to payment"}
              </Button>
            </>
          }
        >
          <Notice warn title="This ends your free trial">
            Switching to{" "}
            <strong>
              {tierName(tier.tier)} (${tier.price_usd}/month)
            </strong>{" "}
            starts a paid subscription now. You&apos;ll be charged today and on
            every renewal after.
          </Notice>
        </Modal>
      )}
      {confirm === "switch" && (
        <Modal
          title={`${isUpgrade ? "Upgrade" : "Downgrade"} to ${tierName(tier.tier)}?`}
          onClose={() => setConfirm(null)}
          footer={
            <>
              <Button variant="ghost" onClick={() => setConfirm(null)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={startCheckout}
                busy={checkoutBusy}
              >
                {checkoutBusy
                  ? "Switching…"
                  : `${isUpgrade ? "Upgrade" : "Downgrade"} now`}
              </Button>
            </>
          }
        >
          <p className="per-muted">
            {tierName(tier.tier)} is ${tier.price_usd} a month, with{" "}
            {humanize(tier.week_cap)} tokens removed a week at full strength. It
            starts now.{" "}
            {isUpgrade
              ? "The difference for the rest of this billing period"
              : "Credit for the unused part of this billing period"}{" "}
            goes on your next invoice
            {renewsAt ? `, on ${fmtDate(renewsAt)}` : ""}.
          </p>
        </Modal>
      )}
    </div>
  );
}

function BillingDetails({ billing }: { billing: BillingData }) {
  if (
    !isSubscribed(billing) ||
    !billing.manage_available ||
    !billing.portal_url
  ) {
    return null;
  }
  return (
    <Card
      title="Billing details"
      actions={
        <LinkButton
          size="sm"
          href={billing.portal_url}
          iconRight="arrow-up-right"
        >
          Open portal
        </LinkButton>
      }
    >
      <p className="per-muted">
        Payment method, invoices and receipts live in the Stripe billing portal.
      </p>
    </Card>
  );
}

export function PlanGrid({
  billing,
  authHeaders,
}: {
  billing: BillingData;
  authHeaders: AuthHeaders;
}) {
  const [agent, setAgent] = React.useState<AgentKey>("claude");
  const [planRank, setPlanRank] = React.useState(1);
  const subscribed = isSubscribed(billing);
  const currentPrice =
    billing.tiers.find((t) => t.tier === billing.tier)?.price_usd ?? 0;
  const paid = billing.tiers.filter((t) => t.tier !== "tier-free");

  // Acquisition: pick a coding agent, then the matcher points at the condense
  // tier that fits its plan.
  if (!subscribed) {
    const agentDef = AGENTS[agent];
    const hasMatcher = agentDef.plans.length > 0;
    return (
      <section id="plans" className="per-plans">
        <SectionHead
          title="Choose a plan"
          actions={
            <Segmented
              size="sm"
              toggle
              label="Coding agent"
              value={agent}
              onChange={setAgent}
              options={AGENT_ORDER.map((k) => ({
                value: k,
                label: AGENTS[k].brand,
              }))}
            />
          }
        />
        {hasMatcher && (
          <div className="per-matcher">
            <span className="per-matcher-label">{agentDef.matcherLabel}</span>
            <Segmented
              size="sm"
              toggle
              label={agentDef.matcherLabel}
              value={String(planRank)}
              onChange={(v) => setPlanRank(Number(v))}
              options={agentDef.plans.map((label, i) => ({
                value: String(i + 1),
                label,
              }))}
            />
          </div>
        )}
        <Grid cols={3}>
          {paid.map((t) => {
            const rank = TIER_RANK[t.tier];
            let matchState: "matched" | "dimmed" | undefined;
            if (hasMatcher) {
              matchState = rank
                ? rank === planRank
                  ? "matched"
                  : "dimmed"
                : undefined;
            } else {
              matchState = t.tier === "t20" ? "matched" : undefined;
            }
            return (
              <TierCard
                key={t.tier}
                tier={t}
                current={!hasEnded(billing) && billing.tier === t.tier}
                resubscribe={hasEnded(billing) && billing.tier === t.tier}
                subscribed={false}
                trialAvailable={billing.trial_available}
                currentPrice={currentPrice}
                matchState={matchState}
                showMatchNote={hasMatcher}
                xform={TIER_XFORM[t.tier]}
                xformCapLabel={
                  hasMatcher ? `your ${agentDef.brand} plan` : "your plan"
                }
                onSelect={
                  hasMatcher && rank ? () => setPlanRank(rank) : undefined
                }
                authHeaders={authHeaders}
              />
            );
          })}
        </Grid>
      </section>
    );
  }

  // Management: every plan, the current one marked by its pill.
  const next = nextTierUp(billing.tiers, billing.tier)?.tier;

  return (
    <section id="plans" className="per-plans">
      <SectionHead title="Plans" />
      <Grid cols={3}>
        {paid.map((t) => (
          <TierCard
            key={t.tier}
            tier={t}
            current={billing.tier === t.tier}
            subscribed
            trialing={billing.is_trial}
            currentPrice={currentPrice}
            xform={TIER_XFORM[t.tier]}
            renewsAt={billing.renews_at}
            featured={t.tier === next}
            authHeaders={authHeaders}
          />
        ))}
      </Grid>
    </section>
  );
}

function ValueTileView({ value }: { value: UsageValue | null | undefined }) {
  if (value === undefined)
    return (
      <div className="ds-stat" aria-busy>
        <Skeleton lines={3} />
      </div>
    );
  const tile = valueTile(value);
  if (!tile) return null;
  return (
    <StatTile
      label={tile.label}
      info={tile.info}
      value={tile.value}
      note={tile.note}
      onClick={() => {
        window.location.hash = "usage";
      }}
    />
  );
}

export function Billing({ authHeaders }: { authHeaders: AuthHeaders }) {
  const [billing, setBilling] = React.useState<BillingData | null>(null);
  const [value, setValue] = React.useState<UsageValue | null | undefined>(
    undefined,
  );
  const [err, setErr] = React.useState<string | null>(null);
  const [attempt, retry] = React.useReducer((n: number) => n + 1, 0);

  React.useEffect(() => {
    let live = true;
    setErr(null);
    getBilling(authHeaders)
      .then((b) => live && setBilling(b))
      .catch(() => live && setErr("Could not load billing."));
    getUsageValue(authHeaders, presetRange("30"))
      .then((v) => live && setValue(v))
      .catch(() => live && setValue(null));
    return () => {
      live = false;
    };
  }, [authHeaders, attempt]);

  React.useEffect(() => {
    if (!billing || getHashParam("plans") !== "1") return;
    setHashParam("plans", null);
    document
      .getElementById("plans")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [billing]);

  // Someone who has never had a plan has nothing to manage yet: the trial
  // offer is the page.
  const fresh =
    !!billing && !billing.tier && !billing.status && billing.trial_available;
  const subscribed = !!billing && isSubscribed(billing);
  const showValue = subscribed && valueTile(value) !== null;

  return (
    <Page title="Subscription">
      {err && (
        <EmptyState
          icon="alert"
          title={err}
          action={<Button onClick={retry}>Retry</Button>}
        />
      )}
      {!billing && !err && <Skeleton lines={5} />}

      {billing?.org && (
        <EmptyState icon="users" title="Billed through your organization">
          Subscriptions are managed by your org admin.
        </EmptyState>
      )}

      {billing && !billing.org && !billing.enabled && (
        <EmptyState title="Subscriptions aren't available yet" />
      )}

      {billing && !billing.org && billing.enabled && (
        <>
          {!fresh && (
            <div
              className={
                billing.tier
                  ? `per-plan-row${showValue || (subscribed && value === undefined) ? " has-value" : ""}`
                  : undefined
              }
            >
              <PlanHeader billing={billing} authHeaders={authHeaders} />
              <PlanCapacity billing={billing} />
              {subscribed && <ValueTileView value={value} />}
            </div>
          )}
          <CapacityNotices billing={billing} />
          <PlanGrid billing={billing} authHeaders={authHeaders} />
          <BillingDetails billing={billing} />
        </>
      )}
    </Page>
  );
}
